import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import { Pagination } from "@/components/molecules/Pagination";
import { PaginationType } from "@/models/common";
import { CarList, CarSortKey, CarOrder } from "@/models/vehicle";
import { TableContainer } from "@mui/material";
import { VehicleConstants, lastCollectedAt } from "./VehicleConstants";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { TableSortLabel } from "@/components/atoms/TableSortLabel";

export type VehicleListProps = {
  onChangePage: (page: number) => void;
  pagingData: PaginationType;
  cars: CarList[];
  orderTypes: CarOrder;
  onClickChangeSort: (key: CarSortKey) => void;
  onClickDetail: (id: number) => void;
};

export const VehicleList = (props: VehicleListProps) => {
  const {
    onChangePage,
    onClickChangeSort,
    onClickDetail,
    cars,
    pagingData,
    orderTypes,
  } = props;

  const [isSmartDisplayEnabled, setIsSmartDisplayEnabled] =
    React.useState(false);
  const [smartDisplayBaseTime, setSmartDisplayBaseTime] =
    React.useState<number | null>(null);

  React.useEffect(() => {
    if (!isSmartDisplayEnabled) {
      return;
    }

    const updateBaseTime = () => setSmartDisplayBaseTime(Date.now());
    updateBaseTime();

    const intervalId = window.setInterval(updateBaseTime, 60 * 1000);
    return () => window.clearInterval(intervalId);
  }, [isSmartDisplayEnabled]);

  const handleChangeSmartDisplay = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsSmartDisplayEnabled(checked);
    setSmartDisplayBaseTime(checked ? Date.now() : null);
  };

  const formatSocCollectedAt = (value?: string) => {
    if (value == null) {
      return lastCollectedAt("short", value);
    }

    if (isSmartDisplayEnabled && smartDisplayBaseTime != null) {
      const targetTime = new Date(value).getTime();

      if (!Number.isNaN(targetTime)) {
        const diffMinutes = Math.floor(
          (smartDisplayBaseTime - targetTime) / (60 * 1000)
        );

        if (diffMinutes >= 0 && diffMinutes <= 60) {
          if (diffMinutes < 60) {
            return `${diffMinutes}分前`;
          } else {
            return "1時間前";
          }
        }
      }
    }

    return lastCollectedAt("short", value);
  };

  return (
    <React.Fragment>
      <TableContainer className="table-container-width">
        <Table size="small" className="list-head">
          <TableHead>
            <TableRow>
              <TableCell colSpan={6} className="body-none-custom" />
              <TableCell className="body-none-custom">
                <Tooltip title={VehicleConstants.SmartDisplayTooltip}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    slotProps={{ typography: { fontSize: "small", fontWeight: "bold" } }}
                    control={
                      <Checkbox
                        size="small"
                        checked={isSmartDisplayEnabled}
                        onChange={handleChangeSmartDisplay}
                      />
                    }
                    label={VehicleConstants.SmartDisplay}
                  />
                </Tooltip>
              </TableCell>
              <TableCell colSpan={3} className="body-none-custom">
                {VehicleConstants.WorkCycle}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {VehicleConstants.CompanyOffice}
                <TableSortLabel
                  active={
                    cars.length && !!orderTypes["officeDisplayName"]
                      ? true
                      : false
                  }
                  direction={
                    orderTypes["officeDisplayName"] === "desc" ? "desc" : "asc"
                  }
                  sx={{ ":hover": { color: "red" } }}
                  onClick={() => onClickChangeSort("officeDisplayName")}
                />
              </TableCell>
              <TableCell>
                {VehicleConstants.RadioNumber}
                <TableSortLabel
                  active={
                    cars.length && !!orderTypes.vehicleCode ? true : false
                  }
                  direction={orderTypes.vehicleCode === "desc" ? "desc" : "asc"}
                  sx={{ ":hover": { color: "red" } }}
                  onClick={() => onClickChangeSort("vehicleCode")}
                />
              </TableCell>
              <TableCell>{VehicleConstants.PlateNumber}</TableCell>
              <TableCell>
                {VehicleConstants.CarModelName}
                <TableSortLabel
                  active={
                    cars.length && !!orderTypes["carModelsDisplayName"]
                      ? true
                      : false
                  }
                  direction={
                    orderTypes["carModelsDisplayName"] === "desc"
                      ? "desc"
                      : "asc"
                  }
                  sx={{ ":hover": { color: "red" } }}
                  onClick={() => onClickChangeSort("carModelsDisplayName")}
                />
              </TableCell>
              <TableCell sx={{ whiteSpace: "pre-line" }}>
                {VehicleConstants.ChargePlanningSystemUsageStartDate.split(
                  "ム"
                ).join("ム\n")}
                <TableSortLabel
                  active={
                    cars.length && !!orderTypes["usageStartOn"] ? true : false
                  }
                  direction={
                    orderTypes["usageStartOn"] === "desc" ? "desc" : "asc"
                  }
                  sx={{ ":hover": { color: "red" } }}
                  onClick={() => onClickChangeSort("usageStartOn")}
                />
              </TableCell>
              <TableCell>
                {VehicleConstants.ResidualPowerPercent}
                <TableSortLabel
                  active={cars.length && !!orderTypes.socPercent ? true : false}
                  direction={orderTypes.socPercent === "desc" ? "desc" : "asc"}
                  sx={{ ":hover": { color: "red" } }}
                  onClick={() => onClickChangeSort("socPercent")}
                />
              </TableCell>
              <TableCell>{VehicleConstants.SocLastCollectedAt}</TableCell>
              <TableCell>{VehicleConstants.Front}</TableCell>
              <TableCell colSpan={2}>{VehicleConstants.Rear}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cars.map((row) => (
              <TableRow key={row.id} hover sx={{ wordBreak: "break-all" }}>
                <TableCell>{row.officeDisplayName}</TableCell>
                <TableCell>{row.vehicleCode}</TableCell>
                <TableCell>{row.plateNumberFullText ?? ""}</TableCell>
                <TableCell>{row.carModelsDisplayName ?? ""}</TableCell>
                <TableCell>
                  {row.usageStartOn?.replace(/-/g, "/") ??
                    VehicleConstants.NotYet}
                </TableCell>
                <TableCell>{row.socPercent ?? ""}</TableCell>
                <TableCell>
                  {formatSocCollectedAt(row.socLastCollectedAt)}
                </TableCell>
                <TableCell>{row.frontShortName ?? ""}</TableCell>
                <TableCell>{row.backShortName ?? ""}</TableCell>
                <TableCell sx={{ textAlign: "right", pr: 0 }}>
                  <CommonButton
                    btnType="Detail"
                    onClickBtn={() => onClickDetail(row.id || 0)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        total={pagingData.total}
        page={pagingData.page}
        size={pagingData.size || 3}
        setPage={onChangePage}
      />
    </React.Fragment>
  );
};
